import React, {useEffect, useRef} from 'react';
import {DateTime} from 'luxon';
import echarts from '@/utils/echartsCore';
import i18n from 'i18n';

export interface AuditEventTimelineSeriesRow {
    auditEvent: string;
    data: number[];
    isOther?: boolean;
}

export interface UserAuditAuditEventsTimelineProps {
    dayKeys: string[];
    series: AuditEventTimelineSeriesRow[];
}

const CHART_HEIGHT_PX = 360;
const TARGET_AXIS_LABELS = 12;

function tooltipDayTitle(dayKey: string): string {
    try {
        return DateTime.fromISO(dayKey, {zone: 'utc'}).toLocaleString(DateTime.DATE_MED);
    } catch {
        return dayKey;
    }
}

function seriesDisplayName(s: AuditEventTimelineSeriesRow): string {
    if (s.isOther) {
        return i18n.t('ub_chart_audit_events_other.text');
    }
    const base = (s.auditEvent ?? '').trim() || '—';
    return base.length > 36 ? `${base.slice(0, 36)}…` : base;
}

const UserAuditAuditEventsTimeline: React.FC<UserAuditAuditEventsTimelineProps> = ({dayKeys, series}) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const showChart = series.length > 0 && dayKeys.length > 0;

    useEffect(() => {
        const el = chartRef.current;
        if (!el || !showChart) {
            return;
        }
        const chart = echarts.init(el);
        const handleResize = () => {
            if (!chart.isDisposed()) {
                chart.resize();
            }
        };
        let resizeObserver: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(handleResize);
            resizeObserver.observe(el);
        }
        window.addEventListener('resize', handleResize);

        const axisLabels = dayKeys.map((k) => {
            try {
                return DateTime.fromISO(k, {zone: 'utc'}).toLocaleString({
                    month: 'short',
                    day: 'numeric',
                });
            } catch {
                return k.length >= 10 ? k.slice(5) : k;
            }
        });
        const dayCount = axisLabels.length;
        const labelInterval = Math.max(0, Math.ceil(dayCount / TARGET_AXIS_LABELS) - 1);
        const rotate = dayCount > 10 ? 28 : 0;
        const axisLabel = {
            rotate,
            interval: labelInterval,
            hideOverlap: true,
        };

        const tooltipAxis = {
            trigger: 'axis' as const,
            axisPointer: {type: 'shadow' as const},
            formatter: (params: Array<{dataIndex: number; marker: string; seriesName: string; value: number}>) => {
                if (!params?.length) {
                    return '';
                }
                const idx = params[0].dataIndex;
                const title = tooltipDayTitle(dayKeys[idx] ?? '');
                const lines = params
                    .filter((p) => p.value > 0)
                    .map((p) => `${p.marker} ${p.seriesName}: <strong>${p.value}</strong>`);
                if (!lines.length) {
                    return `${title}<br/><span class="text-muted">${i18n.t('ub_audit_events_no_activity_day.text')}</span>`;
                }
                return `${title}<br/>${lines.join('<br/>')}`;
            },
        };

        chart.setOption(
            {
                textStyle: {fontFamily: '"Rubik", sans-serif', fontSize: 12},
                tooltip: tooltipAxis,
                legend: {
                    type: 'scroll',
                    bottom: 0,
                    data: series.map((s) => seriesDisplayName(s)),
                },
                grid: {left: 48, right: 12, top: 12, bottom: rotate ? 80 : 56},
                xAxis: {
                    type: 'category',
                    data: axisLabels,
                    axisLabel,
                },
                yAxis: {type: 'value', minInterval: 1},
                series: series.map((s) => ({
                    name: seriesDisplayName(s),
                    type: 'bar' as const,
                    stack: 'events',
                    emphasis: {focus: 'series' as const},
                    data: s.data,
                })),
            },
            true,
        );

        return () => {
            window.removeEventListener('resize', handleResize);
            resizeObserver?.disconnect();
            chart.dispose();
        };
    }, [dayKeys, series, showChart]);

    if (!showChart) {
        return null;
    }

    return (
        <div ref={chartRef} style={{width: '100%', height: CHART_HEIGHT_PX}} />
    );
};

export default UserAuditAuditEventsTimeline;
